import {
    type QueryClient,
    queryOptions,
    skipToken,
} from "@tanstack/react-query";
import { createIsomorphicFn, createServerFn } from "@tanstack/react-start";
import { authQueryOptions, resolveExistingViewerRef } from "@/lib/core/auth";
import { withTimeout } from "@/lib/core/with-timeout";
import { readSharedExperimentArms } from "./experiment-arms.server.ts";

const EXPERIMENT_ARMS_TIMEOUT_MS = 1500;

const readViewerExperimentArms = createServerFn({ method: "GET" }).handler(
    async () => {
        const viewer = await resolveExistingViewerRef();
        return viewer ? readSharedExperimentArms(viewer.viewerId) : null;
    },
);

const readExperimentArms = createIsomorphicFn()
    .server((appUserId: string) =>
        withTimeout(
            readSharedExperimentArms(appUserId),
            EXPERIMENT_ARMS_TIMEOUT_MS,
        ),
    )
    .client(
        async (_appUserId: string) => (await readViewerExperimentArms()) ?? {},
    );

export function experimentArmProperty(name: string) {
    return `${name.replaceAll("-", "_")}_arm`;
}

export function experimentArmsQueryOptions(appUserId: string | null) {
    return queryOptions({
        queryKey: ["experiment-arms", appUserId] as const,
        queryFn: appUserId ? () => readExperimentArms(appUserId) : skipToken,
        retry: false,
        staleTime: Number.POSITIVE_INFINITY,
        gcTime: Number.POSITIVE_INFINITY,
    });
}

export const ensureExperimentArms = createIsomorphicFn()
    .server(async (queryClient: QueryClient) => {
        const appUserId = queryClient.getQueryData(
            authQueryOptions().queryKey,
        )?.appUserId;
        if (!appUserId) return;
        await queryClient
            .ensureQueryData(experimentArmsQueryOptions(appUserId))
            .catch(() => null);
    })
    .client((_queryClient: QueryClient) => Promise.resolve());
